/* Discover page — interaction layer
 * Loaded by sections/main-page-discover.liquid (defer)
 *
 * Responsibilities:
 *   1. Reveal animations via IntersectionObserver
 *   2. Carousel prev/next buttons + keyboard navigation
 *   3. Lazy-image promotion on carousel scroll
 *   4. Click instrumentation (CustomEvent + dataLayer)
 *
 * Event contract for analytics:
 *   window.addEventListener('dc:nav', (e) => {
 *     // e.detail = { section: 'hero|movement|country|century|museums|city|cta|quickpath|movement-index', item: 'slug' }
 *     // dataLayer.push({ event: 'discover_nav_click', ...e.detail });
 *   });
 *   Semantic GA4/dataLayer events are also emitted for dataset/API/source/
 *   directions/study interactions via data-dc-event.
 *
 * Budget: <5KB minified.
 */

(function () {
  'use strict';

  // Captured HERE, synchronously, at module-init time — document.currentScript
  // is only valid while THIS script is executing top-to-bottom; by the time
  // init() runs off a DOMContentLoaded callback (the `document.readyState ===
  // 'loading'` branch at the bottom of this file), currentScript has already
  // reverted to null. Used by initHubTypeahead() to derive the search index's
  // asset URL — body_html (server/workers/discovery/renderers.js) can't call
  // Liquid's `asset_url` filter, so the client resolves it from this script's
  // own <script src>.
  // Discover theme strings (i18n chunk 10, 2026-09-26). snippets/discover-i18n.liquid
  // sets window.SOLIS_DISCOVER_I18N from locales/*.json (solis_discover.js.*).
  // Every call passes the English text, used when the snippet or a key is
  // missing, so English output is unchanged. %n stands for a number.
  var DC_I18N = (typeof window !== 'undefined' && window.SOLIS_DISCOVER_I18N) || {};
  function dcT(key, english) {
    var v = DC_I18N[key];
    return (typeof v === 'string' && v && v.indexOf('translation missing') === -1) ? v : english;
  }
  function dcTn(key, n, englishOne, englishOther) {
    var cat;
    try { cat = new Intl.PluralRules(DC_I18N.locale || 'en').select(n); } catch (e) { cat = n === 1 ? 'one' : 'other'; }
    return (cat === 'one' ? dcT(key + '_one', englishOne) : dcT(key + '_other', englishOther)).replace('%n', String(n));
  }

  var DC_SCRIPT_SRC = (function () {
    if (document.currentScript && document.currentScript.src) return document.currentScript.src;
    var el = document.querySelector('script[src*="discover.js"]');
    return el ? el.src : '';
  })();

  var prefersReducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ── 1. Reveal observer ──────────────────────────────────────

  function initReveal() {
    if (prefersReducedMotion) {
      document.querySelectorAll('.dc-reveal').forEach(function (el) { el.classList.add('is-in'); });
      return;
    }
    if (typeof IntersectionObserver === 'undefined') {
      document.querySelectorAll('.dc-reveal').forEach(function (el) { el.classList.add('is-in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '100px 0px', threshold: 0.05 });
    document.querySelectorAll('.dc-reveal').forEach(function (el) { io.observe(el); });
    setTimeout(function () {
      document.querySelectorAll('.dc-reveal:not(.is-in)').forEach(function (el) {
        el.classList.add('is-in');
        el.style.opacity = '1';
        el.style.transform = 'none';
      });
    }, 2000);
  }

  // ── 2. Carousel nav (buttons + keyboard) ────────────────────

  function tileWidth(carousel) {
    var first = carousel.querySelector('.dc-carousel__tile, .dc-century-strip__tile');
    if (!first) return 380;
    var gap = parseFloat(getComputedStyle(carousel).columnGap || getComputedStyle(carousel).gap || '12') || 12;
    return first.offsetWidth + gap;
  }

  function updateNavState(carousel, prevBtn, nextBtn) {
    var atStart = carousel.scrollLeft <= 4;
    var atEnd = carousel.scrollLeft + carousel.clientWidth >= carousel.scrollWidth - 4;
    if (prevBtn) prevBtn.disabled = atStart;
    if (nextBtn) nextBtn.disabled = atEnd;
  }

  function initCarousel(wrap) {
    var carousel = wrap.querySelector('.dc-carousel');
    if (!carousel) return;
    var prevBtn = wrap.querySelector('.dc-carousel__nav--prev');
    var nextBtn = wrap.querySelector('.dc-carousel__nav--next');

    function scrollByTile(direction) {
      carousel.scrollBy({ left: direction * tileWidth(carousel), behavior: prefersReducedMotion ? 'auto' : 'smooth' });
    }

    if (prevBtn) prevBtn.addEventListener('click', function () { scrollByTile(-1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { scrollByTile(1); });

    var rafId = null;
    carousel.addEventListener('scroll', function () {
      if (rafId) return;
      rafId = requestAnimationFrame(function () {
        rafId = null;
        updateNavState(carousel, prevBtn, nextBtn);
        promoteVisibleImages(carousel);
      });
    }, { passive: true });

    // Keyboard navigation when carousel or its tiles have focus
    carousel.addEventListener('keydown', function (e) {
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          scrollByTile(-1);
          break;
        case 'ArrowRight':
          e.preventDefault();
          scrollByTile(1);
          break;
        case 'Home':
          e.preventDefault();
          carousel.scrollTo({ left: 0, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
          break;
        case 'End':
          e.preventDefault();
          carousel.scrollTo({ left: carousel.scrollWidth, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
          break;
      }
    });

    // Tile focus → scroll into view (keyboard only).
    // On mouse click, the browser focuses the <a> before navigating; if we
    // scroll on that focus, the scroll animation steals the click and the
    // tile's navigation silently fails. Track pointer activity and skip
    // the scroll when focus was pointer-triggered.
    var pointerActive = false;
    carousel.addEventListener('pointerdown', function () {
      pointerActive = true;
      // Reset shortly after — a click completes within ~300ms; after that
      // any focus is keyboard-driven again.
      setTimeout(function () { pointerActive = false; }, 500);
    }, true);

    carousel.querySelectorAll('a').forEach(function (tile) {
      tile.addEventListener('focus', function () {
        if (pointerActive) return; // don't steal pointer-triggered clicks
        tile.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'nearest', inline: 'center' });
      });
    });

    // Initial state
    updateNavState(carousel, prevBtn, nextBtn);
  }

  function initCarousels() {
    document.querySelectorAll('.dc-carousel__wrap').forEach(initCarousel);
  }

  // ── 3. Lazy-image promoter ──────────────────────────────────

  function promoteVisibleImages(carousel) {
    var rect = carousel.getBoundingClientRect();
    var promoteBefore = window.innerWidth * 2;
    carousel.querySelectorAll('img[loading="lazy"]').forEach(function (img) {
      var imgRect = img.getBoundingClientRect();
      if (imgRect.left - rect.left < promoteBefore) {
        img.loading = 'eager';
      }
    });
  }

  // ── 4. Click instrumentation ────────────────────────────────

  function pushAnalytics(eventName, detail) {
    var payload = {
      event: eventName,
      dc_section: detail.section,
      dc_item: detail.item,
      dc_href: detail.href,
    };
    if (detail.format) payload.dc_format = detail.format;
    if (detail.target) payload.dc_target = detail.target;
    if (window.dataLayer && typeof window.dataLayer.push === 'function') {
      window.dataLayer.push(payload);
    }
    // gtag fires even when GTM is present: the GTM container has no tag for
    // discover_* events, so the dataLayer push alone never reaches GA4 (all
    // counts were zero through 2026-06). If a GTM tag is ever added for these
    // events, remove this call or re-add a guard to avoid double-counting.
    if (typeof window.gtag === 'function') {
      // Param names MUST match the registered GA4 custom dimensions — which are
      // `dc_section` / `dc_item` (created for the dataLayer/GTM path, and matching
      // the dataLayer keys above). They were previously sent here as
      // `discover_section` / `discover_item`, which are NOT registered, so every
      // event's section/item arrived as "(not set)" and no Discover event could be
      // broken down by what was clicked. Keep these names in sync with the
      // dataLayer payload keys and the GA4 customDimensions.
      window.gtag('event', eventName, {
        dc_section: detail.section,
        dc_item: detail.item,
        link_url: detail.href,
        file_extension: detail.format || undefined,
        dc_target: detail.target || undefined,
      });
    }
  }

  function inferredEvent(target, item, href) {
    var explicit = target.getAttribute('data-dc-event');
    if (explicit) return explicit;
    item = item || '';
    href = href || '';
    // Every current renderer tags dataset/api/source/trust/education links with an
    // explicit data-dc-event (see server/workers/discovery/renderers.js). The
    // free-text fallbacks below MUST stay anchored to href structure or a fixed
    // item prefix, not scan for a bare word anywhere in `item` — `item` often
    // embeds a museum/artist/movement slug, which is arbitrary third-party text.
    // A bare /trust/.test(item) matched "museum-national-trust" and
    // "museum-birmingham-museums-trust" (plain "Museum details" nav links) and
    // misclassified them as discover_trust_click — 8/8 of that event's fires in
    // the 2026-06-10..2026-07-07 GA4 window were this false positive, not a real
    // Data & Sourcing panel click. Fixed 2026-07-08.
    if (/dataset-(csv|json)/.test(item) || /\/data\/[^?#]+\.(csv|json)(?:[?#]|$)/.test(href)) return 'discover_dataset_download';
    if (/dataset-api/.test(item) || /\/api\/v1\/cities\//.test(href)) return 'discover_api_click';
    if (/display-evidence/.test(item)) return 'discover_display_evidence_click';
    if (/directions|maps|train|travel/.test(item)) return 'discover_travel_click';
    return 'discover_nav_click';
  }

  function formatFromHref(href, item) {
    var value = item || href || '';
    var m = value.match(/\b(csv|json|api|openapi)\b/i) || value.match(/\.([a-z0-9]+)(?:[?#]|$)/i);
    return m ? m[1].toLowerCase() : null;
  }

  function initInstrumentation() {
    // window + capture phase: solis-site-enhancements.js's hash-scroll fix
    // calls stopImmediatePropagation() in a document-capture listener for
    // in-page anchor chips, which silently killed instrumentation for every
    // quick-path/anchor pill (a second reason discover_* counts were zero).
    // Window-capture runs before any document-level handler can stop it.
    window.addEventListener('click', function (e) {
      var target = e.target && e.target.closest ? e.target.closest('[data-dc-track]') : null;
      if (!target) return;
      var item = target.getAttribute('data-dc-item') || null;
      var href = target.getAttribute('href') || null;
      var detail = {
        section: target.getAttribute('data-dc-track'),
        item: item,
        href: href,
        format: target.getAttribute('data-dc-format') || formatFromHref(href, item),
        target: target.getAttribute('data-dc-target') || null,
      };
      try {
        window.dispatchEvent(new CustomEvent('dc:nav', { detail: detail }));
      } catch (_) { /* old browsers without CustomEvent constructor */ }
      pushAnalytics('discover_nav_click', detail);
      var semanticEvent = inferredEvent(target, item, href);
      if (semanticEvent !== 'discover_nav_click') pushAnalytics(semanticEvent, detail);
    }, { capture: true, passive: true });
  }

  // ── 5. Lightbox ─────────────────────────────────────────────
  // Binds to [data-dc-lightbox-src] elements. Opens a <dialog> with the full-res
  // image, caption, prev/next navigation within the same [data-dc-lightbox-group].
  // Keyboard: Esc closes, ArrowLeft/Right navigate, Home/End jump.

  function initLightbox() {
    var dialog = null;
    var img = null;
    var caption = null;
    var prevBtn = null;
    var nextBtn = null;
    var currentGroup = [];
    var currentIndex = 0;

    function ensureDialog() {
      if (dialog) return;
      dialog = document.createElement('dialog');
      dialog.className = 'dc-lightbox';
      dialog.setAttribute('aria-label', dcT('lightbox_label', 'Image viewer'));
      dialog.innerHTML =
        '<div class="dc-lightbox__body">' +
          '<button type="button" class="dc-lightbox__close" aria-label="' + dcT('lightbox_close', 'Close') + '">\u00D7</button>' +
          '<button type="button" class="dc-lightbox__prev" aria-label="' + dcT('lightbox_prev', 'Previous') + '">\u2039</button>' +
          '<img class="dc-lightbox__img" alt="">' +
          '<button type="button" class="dc-lightbox__next" aria-label="' + dcT('lightbox_next', 'Next') + '">\u203A</button>' +
          '<div class="dc-lightbox__caption" aria-live="polite"></div>' +
        '</div>';
      document.body.appendChild(dialog);
      img = dialog.querySelector('.dc-lightbox__img');
      caption = dialog.querySelector('.dc-lightbox__caption');
      prevBtn = dialog.querySelector('.dc-lightbox__prev');
      nextBtn = dialog.querySelector('.dc-lightbox__next');
      var closeBtn = dialog.querySelector('.dc-lightbox__close');
      closeBtn.addEventListener('click', close);
      prevBtn.addEventListener('click', function () { go(currentIndex - 1); });
      nextBtn.addEventListener('click', function () { go(currentIndex + 1); });
      dialog.addEventListener('click', function (e) { if (e.target === dialog) close(); });
      dialog.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') close();
        else if (e.key === 'ArrowLeft') { e.preventDefault(); go(currentIndex - 1); }
        else if (e.key === 'ArrowRight') { e.preventDefault(); go(currentIndex + 1); }
        else if (e.key === 'Home') { e.preventDefault(); go(0); }
        else if (e.key === 'End') { e.preventDefault(); go(currentGroup.length - 1); }
      });
      // Safari (desktop) can leave the page scroll-locked after a modal <dialog>
      // closes via ESC — it doesn't reliably release its native modal scroll
      // handling. So we manage scroll ourselves: restore it on the native 'close'
      // event, which fires for EVERY close path (ESC, close button, backdrop,
      // programmatic close). Pairs with the lock set in open().
      dialog.addEventListener('close', function () {
        document.documentElement.style.overflow = '';
        document.body.style.overflow = '';
      });
    }

    function buildGroup(trigger) {
      var group = trigger.getAttribute('data-dc-lightbox-group');
      var items = group
        ? Array.prototype.slice.call(document.querySelectorAll('[data-dc-lightbox-src][data-dc-lightbox-group="' + CSS.escape(group) + '"]'))
        : [trigger];
      return {
        items: items,
        index: Math.max(0, items.indexOf(trigger)),
      };
    }

    function go(i) {
      if (currentGroup.length === 0) return;
      var n = currentGroup.length;
      var nextIdx = ((i % n) + n) % n;  // wrap
      currentIndex = nextIdx;
      var t = currentGroup[nextIdx];
      img.src = t.getAttribute('data-dc-lightbox-src') || '';
      img.alt = t.getAttribute('data-dc-lightbox-caption') || '';
      var cap = t.getAttribute('data-dc-lightbox-caption') || '';
      caption.textContent = cap;
      caption.style.display = cap ? '' : 'none';
      var hasMany = n > 1;
      prevBtn.style.display = hasMany ? '' : 'none';
      nextBtn.style.display = hasMany ? '' : 'none';
    }

    function open(trigger) {
      ensureDialog();
      var built = buildGroup(trigger);
      currentGroup = built.items;
      currentIndex = built.index;
      go(currentIndex);
      // Lock background scroll while the lightbox is open; the dialog's 'close'
      // handler restores it. Also fixes the background scrolling behind the
      // lightbox (visible in Chrome) as a side benefit.
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      if (typeof dialog.showModal === 'function') {
        dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
      }
    }

    function close() {
      if (!dialog) return;
      // Restore here too (not just on the 'close' event) so the legacy
      // removeAttribute('open') fallback — which does NOT fire 'close' — also
      // releases the scroll lock. Idempotent with the 'close' listener.
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
      if (typeof dialog.close === 'function') {
        dialog.close();
      } else {
        dialog.removeAttribute('open');
      }
    }

    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-dc-lightbox-src]');
      if (!trigger) return;
      e.preventDefault();
      open(trigger);
    });
  }

  // ── 6. Faceted navigation — URL ?movements=...&centuries=...&nationalities=...
  var FK = ['movements', 'centuries', 'nationalities'];
  var FLABEL = { movements: 'Movement', centuries: 'Era', nationalities: 'Nationality' };
  var CLABEL = { 'renaissance': 'Renaissance', '17th-century': '17th Century', '18th-century': '18th Century', '19th-century': '19th Century', '20th-century': '20th Century' };

  function fSlug(v) { return String(v || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''); }
  function fEsc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }
  // Shopify CDN srcset builder — matches the server's responsiveImg output for
  // cdn.shopify.com URLs so materialised mtiles look like server-rendered ones.
  // Non-Shopify URLs (Wikimedia fallback etc.) keep the single-src form.
  // Shared between facet-nav and artists-index materialisers.
  var FN_SHOPIFY_PREFIX = 'https://cdn.shopify.com/s/files/1/0833/9292/1935/files/';
  function fnExpand(u) {
    if (!u) return u;
    return u.charAt(0) === '~' ? FN_SHOPIFY_PREFIX + u.slice(1) : u;
  }
  function fImg(url, alt) {
    var a = 'class="dc-mtile__img" alt="' + fEsc(alt) + '" loading="lazy" decoding="async"';
    var expanded = fnExpand(url);
    if (expanded && expanded.indexOf('cdn.shopify.com') > -1) {
      var c = expanded.split('?')[0];
      var ss = [240, 320, 480, 640, 960].map(function (w) { return c + '?width=' + w + ' ' + w + 'w'; }).join(', ');
      return '<img src="' + fEsc(c + '?width=480') + '" srcset="' + fEsc(ss) + '" sizes="(max-width:640px) 50vw, 240px" ' + a + '>';
    }
    return '<img src="' + fEsc(expanded) + '" ' + a + '>';
  }

  function initFacetedNav() {
    // /pages/artists-index has its own dropdown-style filter bar handled by
    // initArtistsIndexFilter; skip this chip-row pattern when that's present.
    if (document.querySelector('.dc-aif-section')) return;
    var scriptEl = document.getElementById('dc-artist-manifest');
    if (!scriptEl) return;
    var data;
    try { data = JSON.parse(scriptEl.textContent); } catch (_) { return; }
    if (!data || !(data.artists || []).length) return;
    var grid = document.querySelector('.dc-artist-grid');
    if (!grid) return;
    var facets = data.facets || {};
    if (!FK.some(function (k) { return (facets[k] || []).length; })) return;

    // Index DOM tiles by slug; track whether they were materialised from manifest.
    var idx = {};
    grid.querySelectorAll('[data-slug]').forEach(function (el) {
      var li = el.closest('li');
      if (li) idx[el.getAttribute('data-slug')] = { li: li, m: false };
    });
    var bySlug = {};
    data.artists.forEach(function (a) { bySlug[a.slug] = a; });

    var state = { movements: {}, centuries: {}, nationalities: {} };
    try {
      var params = new URLSearchParams(window.location.search);
      FK.forEach(function (k) {
        var raw = params.get(k);
        if (!raw) return;
        raw.split(',').forEach(function (s) { var sl = fSlug(s); if (sl) state[k][sl] = true; });
      });
    } catch (_) { /* ignore */ }

    var panel = document.createElement('section');
    panel.className = 'dc-section dc-reveal is-in';
    panel.setAttribute('aria-label', 'Filter artists');

    var chips = [];
    // CSS [aria-pressed="true"] on .dc-movement-pill handles the active look;
    // we just flip the attribute.
    function setOn(btn, on) {
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    }

    FK.forEach(function (k) {
      var vals = facets[k];
      if (!vals || !vals.length) return;
      var row = document.createElement('div');
      row.setAttribute('role', 'group');
      row.setAttribute('aria-label', FLABEL[k] + ' filter');
      row.style.cssText = 'margin:0 0 .6rem';
      var lbl = document.createElement('span');
      lbl.textContent = FLABEL[k];
      lbl.style.cssText = 'display:inline-block;margin-right:.6rem;font-size:.8rem;color:var(--dc-text-muted);text-transform:uppercase;letter-spacing:.05em';
      row.appendChild(lbl);
      var bar = document.createElement('span');
      bar.className = 'dc-movement-pills';
      bar.style.display = 'inline-flex';
      vals.forEach(function (v) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'dc-movement-pill';
        btn.setAttribute('data-dc-track', 'facet');
        btn.setAttribute('data-dc-item', k + '-' + fSlug(v));
        var lbl = document.createTextNode(k === 'centuries' ? (CLABEL[v] || v) : v);
        var cnt = document.createElement('span');
        cnt.className = 'dc-movement-pill__period';
        btn.appendChild(lbl);
        btn.appendChild(document.createTextNode(' '));
        btn.appendChild(cnt);
        var sv = fSlug(v);
        setOn(btn, !!state[k][sv]);
        btn.addEventListener('click', function () {
          if (state[k][sv]) { delete state[k][sv]; setOn(btn, false); }
          else { state[k][sv] = true; setOn(btn, true); }
          apply();
        });
        chips.push({ btn: btn, cnt: cnt, k: k, sv: sv });
        bar.appendChild(btn);
      });
      row.appendChild(bar);
      panel.appendChild(row);
    });

    var stat = document.createElement('p');
    stat.className = 'dc-section__lede';
    stat.setAttribute('aria-live', 'polite');
    stat.style.cssText = 'margin:.4rem 0 0;display:inline-block';
    panel.appendChild(stat);

    var clr = document.createElement('button');
    clr.type = 'button';
    clr.className = 'dc-movement-pill';
    clr.textContent = 'Clear filters';
    clr.hidden = true;
    clr.style.marginLeft = '.6rem';
    clr.addEventListener('click', function () {
      FK.forEach(function (k) { state[k] = {}; });
      chips.forEach(function (c) { setOn(c.btn, false); });
      apply();
    });
    panel.appendChild(clr);

    var host = grid.parentNode;
    host.insertBefore(panel, grid);

    var empty = document.createElement('p');
    empty.className = 'dc-section__lede';
    empty.style.cssText = 'text-align:center;margin:1.5rem 0';
    empty.textContent = 'No artists match these filters.';
    empty.hidden = true;
    host.insertBefore(empty, grid.nextSibling);

    function matches(a, st) {
      st = st || state;
      for (var i = 0; i < FK.length; i++) {
        var k = FK[i], s = st[k];
        if (!s || !Object.keys(s).length) continue;
        if (k === 'movements') {
          var movs = a.mov || [], hit = false;
          for (var j = 0; j < movs.length; j++) if (s[fSlug(movs[j])]) { hit = true; break; }
          if (!hit) return false;
        } else if (!s[fSlug((k === 'centuries' ? a.c : a.nat) || '')]) return false;
      }
      return true;
    }

    // Preview count for a chip: how many artists match if we replace this
    // chip's facet with just this one value, keeping other facets as-is.
    function chipCount(k, sv) {
      var preview = {};
      for (var i = 0; i < FK.length; i++) preview[FK[i]] = FK[i] === k ? {} : state[FK[i]];
      preview[k][sv] = true;
      var n = 0;
      for (var j = 0; j < data.artists.length; j++) if (matches(data.artists[j], preview)) n++;
      return n;
    }

    function makeTile(a) {
      var li = document.createElement('li');
      var bits = [];
      if (a.b && a.d) bits.push(a.b + '\u2013' + a.d);
      else if (a.b) bits.push('b. ' + a.b);
      if (a.nat) bits.push(a.nat);
      var meta = bits.length ? '<p class="dc-mtile__meta">' + bits.map(fEsc).join(' \u00B7 ') + '</p>' : '';
      var da = 'data-slug="' + fEsc(a.slug) + '"';
      if (a.nat) da += ' data-nat="' + fEsc(a.nat) + '"';
      if (a.mov && a.mov.length) da += ' data-mov="' + fEsc(a.mov.join('|')) + '"';
      if (a.c) da += ' data-c="' + fEsc(a.c) + '"';
      li.innerHTML = '<a class="dc-mtile" href="' + fEsc(window.SolisMarketPath('/pages/artist-' + a.slug)) + '" ' + da + '>' +
        '<div class="dc-mtile__imgwrap">' + fImg(a.i, a.n) + '</div>' +
        '<div class="dc-mtile__caption"><h3 class="dc-mtile__title">' + fEsc(a.n) + '</h3>' + meta + '</div></a>';
      grid.appendChild(li);
      return { li: li, m: true };
    }

    function apply() {
      var on = FK.some(function (k) { return Object.keys(state[k]).length; });
      var visible = 0;
      if (on) data.artists.forEach(function (a) { if (matches(a) && !idx[a.slug]) idx[a.slug] = makeTile(a); });
      Object.keys(idx).forEach(function (slug) {
        var rec = idx[slug], a = bySlug[slug];
        var show = on ? (a ? matches(a) : false) : !rec.m;
        rec.li.hidden = !show;
        if (show) visible++;
      });
      stat.textContent = on ? 'Showing ' + visible + ' of ' + data.total + ' artists.' : '';
      empty.hidden = !on || visible > 0;
      clr.hidden = !on;
      // Refresh per-chip counts — intersection with other active facets.
      chips.forEach(function (c) {
        var n = chipCount(c.k, c.sv);
        c.cnt.textContent = '(' + n + ')';
      });
      try {
        var u = new URL(window.location.href);
        FK.forEach(function (k) {
          var vs = Object.keys(state[k]);
          if (vs.length) u.searchParams.set(k, vs.join(','));
          else u.searchParams.delete(k);
        });
        window.history.replaceState(null, '', u.pathname + u.search + u.hash);
      } catch (_) { /* ignore */ }
    }
    apply();
  }

  // ── 7. Artists-index dropdown filter UI (replaces A-Z + chip wall) ──
  //
  // Driven by the same manifest as initFacetedNav, but renders dropdown
  // facets (server-rendered <details> elements) + an artist-name search
  // box + an active filter chip row + live result count. Materialises
  // off-DOM artists from the manifest the same way initFacetedNav does.
  function initArtistsIndexFilter() {
    var section = document.querySelector('.dc-aif-section');
    if (!section) return;
    var scriptEl = document.getElementById('dc-artist-manifest');
    if (!scriptEl) return;
    var data;
    try { data = JSON.parse(scriptEl.textContent); } catch (_) { return; }
    if (!data) return;
    // Two manifest shapes (AIF_MANIFEST_SRC_SUPPORT is the capability marker
    // server/workers/discovery/artists-index-asset.js looks for in the LIVE
    // theme's copy of this file before it ships a stub page; keep it):
    // - inline: data.artists holds the roster (pages before 2026-09-26);
    // - stub: data.src names a theme asset beside this script that holds the
    //   roster (manifest.js artistsIndexManifestAsset). The roster outgrew
    //   Shopify's 512KB body_html cap (2,886 artists, 677KB page). The asset is
    //   fetched on the visitor's first touch of the filter bar, or at once when
    //   the URL already carries filters.
    var AIF_MANIFEST_SRC_SUPPORT = 'dc-artists-index-manifest-src:v1';
    var AIF_SRC_PATTERN = /^discover-artists-index-[0-9a-f]{12}\.json$/;
    var hasInline = (data.artists || []).length > 0;
    if (!hasInline && !(typeof data.src === 'string' && AIF_SRC_PATTERN.test(data.src))) return;
    var grid = document.querySelector('#all-artists .dc-artist-grid')
            || document.querySelector('.dc-artist-grid:not(.dc-artist-grid--featured)');
    if (!grid) return;
    section.setAttribute('data-aif-manifest', AIF_MANIFEST_SRC_SUPPORT);

    // The compact roster (manifest.js compactArtistsIndexManifest): nat/mov/era
    // are interned into d.dict and stored as integer indices, and the century
    // slug is dropped. Re-expand to the verbose per-entry shape the rest of
    // this function expects. Other Discover pages ship the verbose manifest
    // (no `dict`) and skip this.
    function expandCompact(d) {
      if (!d.dict) return;
      var DNAT = d.dict.nat || [], DMOV = d.dict.mov || [], DERA = d.dict.era || [];
      d.artists.forEach(function (a) {
        if (typeof a.nat === 'number') a.nat = DNAT[a.nat];
        if (typeof a.era === 'number') a.era = DERA[a.era];
        if (Array.isArray(a.mov)) a.mov = a.mov.map(function (i) { return DMOV[i]; }).filter(Boolean);
      });
    }

    var FACET_KEYS = ['movements', 'eras', 'nationalities'];
    var FACET_LABEL_BY_KEY = { movements: 'Movement', eras: 'Era', nationalities: 'Country' };

    // Index existing DOM tiles by slug; track materialised flag for off-DOM tiles.
    var idx = {};
    grid.querySelectorAll('[data-slug]').forEach(function (el) {
      var li = el.closest('li');
      if (li) idx[el.getAttribute('data-slug')] = { li: li, m: false };
    });
    var bySlug = {};
    var rosterState = hasInline ? 'ready' : 'idle'; // idle | loading | ready | failed
    var rosterPromise = null;
    function useRoster(d) {
      expandCompact(d);
      data.artists = d.artists;
      if (typeof d.total === 'number') data.total = d.total;
      if (d.facets) data.facets = d.facets;
      data.artists.forEach(function (a) { bySlug[a.slug] = a; });
      rosterState = 'ready';
    }
    // The asset sits beside this script in the theme: body_html cannot call
    // Liquid's asset_url, so the URL comes from discover.js's own src (as the
    // search index does). The filename carries a content hash, so the URL is
    // new whenever the roster changes and needs no ?v token.
    function rosterUrl() {
      var base = (DC_SCRIPT_SRC || '').split('?')[0];
      if (!/discover\.js$/.test(base)) return null;
      return base.replace(/discover\.js$/, data.src);
    }
    function loadRoster() {
      if (rosterPromise) return rosterPromise;
      var url = rosterUrl();
      rosterState = 'loading';
      rosterPromise = (url ? fetch(url, { credentials: 'omit' }) : Promise.reject(new Error('no discover.js src')))
        .then(function (r) { if (!r.ok) throw new Error('bad status'); return r.json(); })
        .then(function (d) {
          if (!d || !Array.isArray(d.artists) || !d.artists.length) throw new Error('not a roster');
          useRoster(d);
        })
        .catch(function () { rosterState = 'failed'; });
      return rosterPromise;
    }
    if (hasInline) useRoster(data);

    // Era display labels — the server uses bucket slugs (pre-1700, 18th-19th-c, …).
    // We re-label here on the option text instead of round-tripping through the server.
    var ERA_LABELS = {
      'pre-1700': 'Pre-1700',
      '18th-19th-c': '18th–19th Century',
      'late-19th-c': 'Late 19th Century',
      '20th-c': '20th Century',
      'contemporary': 'Contemporary'
    };

    // ── State (read from URL) ──
    var state = { movements: {}, eras: {}, nationalities: {} };
    var search = '';
    try {
      var params = new URLSearchParams(window.location.search);
      FACET_KEYS.forEach(function (k) {
        var raw = params.get(k);
        if (!raw) return;
        raw.split(',').forEach(function (s) { var sl = fSlug(s); if (sl) state[k][sl] = true; });
      });
      var q = (params.get('q') || '').trim();
      if (q) search = q.toLowerCase();
    } catch (_) { /* ignore */ }

    // ── DOM refs ──
    var clearBtn = section.querySelector('.dc-aif-clear');
    var activeRow = section.querySelector('.dc-aif-active');
    var activeChips = section.querySelector('.dc-aif-active__chips');
    var status = section.querySelector('.dc-aif-status');
    var searchInput = section.querySelector('#dc-artist-search');
    var emptyMsg = null; // lazily created when no matches

    if (searchInput && search) searchInput.value = search;

    // ── Match logic ──
    function nameMatchesSearch(a) {
      if (!search) return true;
      var name = (a.n || '').toLowerCase();
      return name.indexOf(search) !== -1;
    }
    function matchesFacets(a, st) {
      st = st || state;
      for (var i = 0; i < FACET_KEYS.length; i++) {
        var k = FACET_KEYS[i];
        var s = st[k];
        if (!s || !Object.keys(s).length) continue;
        var present = false;
        if (k === 'movements') {
          var movs = a.mov || [];
          for (var j = 0; j < movs.length; j++) if (s[fSlug(movs[j])]) { present = true; break; }
        } else if (k === 'eras') {
          present = !!s[fSlug(a.era || '')];
        } else if (k === 'nationalities') {
          present = !!s[fSlug(a.nat || '')];
        }
        if (!present) return false;
      }
      return true;
    }
    function matches(a, st) {
      return matchesFacets(a, st) && nameMatchesSearch(a);
    }

    // ── Tile materialisation (off-DOM artists become DOM when filtered in) ──
    // Manifest compresses Solis Shopify CDN URLs to a `~filename` token to
    // save ~120KB on the page (the manifest carries 1,747 artists; full
    // URLs were ~70 chars of repeated prefix). Re-expand here before render.
    var SHOPIFY_PREFIX = 'https://cdn.shopify.com/s/files/1/0833/9292/1935/files/';
    function expandImgUrl(u) {
      if (!u) return u;
      if (u.charAt(0) === '~') return SHOPIFY_PREFIX + u.slice(1);
      return u;
    }
    function fImgInline(url, alt) {
      var common = 'class="dc-mtile__img" alt="' + fEsc(alt) + '" loading="lazy" decoding="async"';
      var expanded = expandImgUrl(url);
      if (expanded && expanded.indexOf('cdn.shopify.com') > -1) {
        var c = expanded.split('?')[0];
        var ss = [240, 320, 480, 640, 960].map(function (w) { return c + '?width=' + w + ' ' + w + 'w'; }).join(', ');
        return '<img src="' + fEsc(c + '?width=480') + '" srcset="' + fEsc(ss) + '" sizes="(max-width:640px) 50vw, 240px" ' + common + '>';
      }
      return '<img src="' + fEsc(expanded) + '" ' + common + '>';
    }
    function makeTile(a) {
      var li = document.createElement('li');
      var bits = [];
      if (a.b && a.d) bits.push(a.b + '–' + a.d);
      else if (a.b) bits.push('b. ' + a.b);
      if (a.nat) bits.push(a.nat);
      var meta = bits.length ? '<p class="dc-mtile__meta">' + bits.map(fEsc).join(' · ') + '</p>' : '';
      var da = 'data-slug="' + fEsc(a.slug) + '"';
      if (a.nat) da += ' data-nat="' + fEsc(a.nat) + '"';
      if (a.mov && a.mov.length) da += ' data-mov="' + fEsc(a.mov.join('|')) + '"';
      if (a.era) da += ' data-era="' + fEsc(a.era) + '"';
      li.innerHTML = '<a class="dc-mtile" href="' + fEsc(window.SolisMarketPath('/pages/artist-' + a.slug)) + '" ' + da + '>' +
        '<div class="dc-mtile__imgwrap">' + fImgInline(a.i, a.n) + '</div>' +
        '<div class="dc-mtile__caption"><h3 class="dc-mtile__title">' + fEsc(a.n) + '</h3>' + meta + '</div></a>';
      grid.appendChild(li);
      return { li: li, m: true };
    }

    // ── URL state sync ──
    function syncURL() {
      try {
        var u = new URL(window.location.href);
        FACET_KEYS.forEach(function (k) {
          var vs = Object.keys(state[k]);
          if (vs.length) u.searchParams.set(k, vs.join(','));
          else u.searchParams.delete(k);
        });
        if (search) u.searchParams.set('q', search);
        else u.searchParams.delete('q');
        window.history.replaceState(null, '', u.pathname + u.search + u.hash);
      } catch (_) { /* ignore */ }
    }

    // ── Active chip row ──
    function renderActiveChips() {
      if (!activeRow || !activeChips) return;
      activeChips.innerHTML = '';
      var hasAny = false;
      FACET_KEYS.forEach(function (k) {
        var vs = Object.keys(state[k]);
        if (!vs.length) return;
        // We need the original (non-slugged) value to label the chip — look it up
        // from facets array.
        var faceted = (data.facets || {})[k] || [];
        vs.forEach(function (sv) {
          var orig = faceted.find(function (v) { return fSlug(v) === sv; }) || sv;
          var display = (k === 'eras' && ERA_LABELS[orig]) ? ERA_LABELS[orig] : orig;
          var chip = document.createElement('span');
          chip.className = 'dc-aif-chip';
          chip.appendChild(document.createTextNode(display));
          var rm = document.createElement('button');
          rm.type = 'button';
          rm.className = 'dc-aif-chip__remove';
          rm.setAttribute('aria-label', 'Remove ' + display + ' filter');
          rm.textContent = '×';
          rm.addEventListener('click', function () { delete state[k][sv]; apply(); });
          chip.appendChild(rm);
          activeChips.appendChild(chip);
          hasAny = true;
        });
      });
      activeRow.hidden = !hasAny;
    }

    // ── Empty state for the grid ──
    function ensureEmptyMsg() {
      if (emptyMsg) return emptyMsg;
      emptyMsg = document.createElement('p');
      emptyMsg.className = 'dc-aif-empty';
      emptyMsg.textContent = 'No artists match these filters.';
      emptyMsg.hidden = true;
      grid.parentNode.insertBefore(emptyMsg, grid.nextSibling);
      return emptyMsg;
    }

    // ── Apply: filter, materialise, update counts, status, URL ──
    function apply() {
      var hasFilters = FACET_KEYS.some(function (k) { return Object.keys(state[k]).length; });
      var hasSearch = !!search;
      var anyActive = hasFilters || hasSearch;

      // Stub page: the roster arrives from the theme asset. Until then the
      // server-rendered tiles stay as they are and the status says why; the
      // chips, badges and URL below still follow the choice.
      var waiting = anyActive && rosterState !== 'ready';
      if (waiting && rosterState !== 'failed') loadRoster().then(apply);

      // Materialise off-DOM matches when any filter is active.
      if (anyActive && !waiting) {
        data.artists.forEach(function (a) {
          if (matches(a) && !idx[a.slug]) idx[a.slug] = makeTile(a);
        });
      }

      var visible = 0;
      if (!waiting) Object.keys(idx).forEach(function (slug) {
        var rec = idx[slug];
        var a = bySlug[slug];
        var show;
        if (anyActive) {
          show = a ? matches(a) : false;
        } else {
          // Default state — show server-rendered tiles only (matches old behaviour).
          show = !rec.m;
        }
        rec.li.hidden = !show;
        if (show) visible++;
      });

      // Status text + empty state
      if (status) {
        if (waiting) {
          status.textContent = rosterState === 'failed'
            ? 'The artist filters could not load. Refresh the page to try again.'
            : 'Loading artists\u2026';
        } else if (anyActive) {
          status.innerHTML = '<strong>' + visible + '</strong> of ' + data.total + ' artists';
        } else {
          status.textContent = '';
        }
      }
      var em = ensureEmptyMsg();
      em.hidden = !anyActive || waiting || visible > 0;

      // Active chip row
      renderActiveChips();

      // Per-dropdown active count badge + open/closed reflect of active state
      section.querySelectorAll('.dc-aif').forEach(function (det) {
        var k = det.getAttribute('data-facet');
        var n = Object.keys(state[k] || {}).length;
        if (n > 0) {
          det.setAttribute('data-active', 'true');
          // Append count badge if not already there
          var summary = det.querySelector('.dc-aif__summary');
          var badge = summary.querySelector('.dc-aif__active-count');
          if (!badge) {
            badge = document.createElement('span');
            badge.className = 'dc-aif__active-count';
            // Insert before the chevron
            var chev = summary.querySelector('.dc-aif__chevron');
            summary.insertBefore(badge, chev);
          }
          badge.textContent = String(n);
        } else {
          det.removeAttribute('data-active');
          var b = det.querySelector('.dc-aif__active-count');
          if (b) b.remove();
        }
        // Update aria-pressed on each option to reflect state
        det.querySelectorAll('.dc-aif__opt').forEach(function (btn) {
          var sv = fSlug(btn.getAttribute('data-value'));
          btn.setAttribute('aria-pressed', state[k][sv] ? 'true' : 'false');
        });
      });

      // Clear-all visibility
      if (clearBtn) clearBtn.hidden = !anyActive;

      // URL state
      syncURL();
    }

    // ── Wire up dropdown option clicks ──
    section.querySelectorAll('.dc-aif__opt').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var k = btn.getAttribute('data-facet');
        var sv = fSlug(btn.getAttribute('data-value'));
        if (state[k][sv]) delete state[k][sv];
        else state[k][sv] = true;
        apply();
      });
    });

    // ── Wire up Clear-all ──
    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        FACET_KEYS.forEach(function (k) { state[k] = {}; });
        search = '';
        if (searchInput) searchInput.value = '';
        apply();
      });
    }

    // ── Wire up search input (debounced) ──
    if (searchInput) {
      var t = null;
      searchInput.addEventListener('input', function () {
        clearTimeout(t);
        t = setTimeout(function () {
          search = searchInput.value.trim().toLowerCase();
          apply();
        }, 150);
      });
    }

    // ── Click outside closes any open dropdowns ──
    document.addEventListener('click', function (e) {
      section.querySelectorAll('.dc-aif[open]').forEach(function (det) {
        if (!det.contains(e.target)) det.removeAttribute('open');
      });
    });
    // ESC closes the focused dropdown
    section.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        section.querySelectorAll('.dc-aif[open]').forEach(function (det) {
          det.removeAttribute('open');
        });
        if (document.activeElement && document.activeElement.classList && document.activeElement.classList.contains('dc-aif__opt')) {
          var summary = document.activeElement.closest('.dc-aif').querySelector('.dc-aif__summary');
          if (summary) summary.focus();
        }
      }
    });

    // Stub page: start the roster download on the first touch of the filter
    // bar, so it is usually there by the time a filter is chosen.
    if (rosterState === 'idle') {
      var prefetch = function () {
        loadRoster();
        section.removeEventListener('pointerdown', prefetch);
        section.removeEventListener('focusin', prefetch);
      };
      section.addEventListener('pointerdown', prefetch);
      section.addEventListener('focusin', prefetch);
    }

    // Initial apply (handles deep-links with URL params)
    apply();
  }

  // ── Boot ────────────────────────────────────────────────────

  // ── 8. Inline citation hover/tap preview cards ──
  //
  // Decorates every <sup class="dc-cite"> with a floating card on hover
  // (desktop) or a bottom-sheet on tap (mobile). Card content is read from
  // the data-cite-card JSON attribute the renderer emits, so no extra
  // network call. Click takes the user to the numbered footer anchor.
  function initCiteCards() {
    var cites = document.querySelectorAll('.dc-cite');
    if (!cites.length) return;
    var card = null;
    var hideTimer = null;
    var isMobile = window.matchMedia('(max-width: 640px)').matches;

    function buildCard(data) {
      var c = document.createElement('div');
      c.className = 'dc-cite-card';
      c.setAttribute('role', 'tooltip');
      var bits = [];
      if (data.author) bits.push(data.author);
      if (data.publisher) bits.push(data.publisher);
      if (data.year) bits.push(String(data.year));
      var meta = bits.length ? '<span class="dc-cite-card__meta">' + bits.join(' · ') + '</span>' : '';
      var used = data.used_for ? '<span class="dc-cite-card__used">' + dcT('cite_used_for', 'Used for:') + ' ' + data.used_for + '</span>' : '';
      var openLink = '<a class="dc-cite-card__open" href="#dc-source-' + data.n + '">' + dcT('cite_open', 'Open in sources →') + '</a>';
      c.innerHTML =
        '<span class="dc-cite-card__num">[' + data.n + ']</span>' +
        '<span class="dc-cite-card__title">' + (data.title || dcT('cite_source', 'Source')) + '</span>' +
        meta +
        used +
        openLink;
      return c;
    }

    function show(supEl) {
      hide();
      var raw = supEl.getAttribute('data-cite-card');
      if (!raw) return;
      var data;
      try { data = JSON.parse(raw); } catch (_) { return; }
      card = buildCard(data);
      document.body.appendChild(card);
      if (!isMobile) {
        var rect = supEl.getBoundingClientRect();
        var top = rect.bottom + window.scrollY + 6;
        var left = rect.left + window.scrollX - 120;
        if (left < 12) left = 12;
        if (left + 320 > window.innerWidth - 12) left = window.innerWidth - 320 - 12;
        card.style.top = top + 'px';
        card.style.left = left + 'px';
      }
      // Hover bridge: keep card visible while user hovers it
      card.addEventListener('mouseenter', function () { clearTimeout(hideTimer); });
      card.addEventListener('mouseleave', function () { hideTimer = setTimeout(hide, 200); });
    }

    function hide() {
      if (card && card.parentNode) card.parentNode.removeChild(card);
      card = null;
    }

    cites.forEach(function (supEl) {
      supEl.addEventListener('mouseenter', function () {
        clearTimeout(hideTimer);
        show(supEl);
      });
      supEl.addEventListener('mouseleave', function () {
        hideTimer = setTimeout(hide, 200);
      });
      // Touch / click — open the card; second tap follows the link.
      supEl.addEventListener('click', function (e) {
        if (isMobile) {
          if (!card) {
            e.preventDefault();
            show(supEl);
          }
        }
        // Desktop click goes through to #dc-source-N anchor
      });
    });

    // Tap outside closes the card (mobile)
    document.addEventListener('click', function (e) {
      if (!card) return;
      if (e.target.closest('.dc-cite') || e.target.closest('.dc-cite-card')) return;
      hide();
    });
    // ESC closes
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') hide();
    });
  }

  // ── 11. Klaviyo suppression on Discover ─────────────────────
  // The newsletter popup is commerce-oriented. Discover pages are editorial,
  // so keep the form off this surface even if Klaviyo's global onsite script
  // is injected by the Shopify app embed.
  function initKlaviyoSuppression() {
    var formId = 'ULNbNs';
    var markers = ['Art worth knowing about', 'GET THE ART EDIT', 'NL12'];

    function addCssGuard() {
      if (document.getElementById('dc-klaviyo-suppression')) return;
      var style = document.createElement('style');
      style.id = 'dc-klaviyo-suppression';
      style.textContent = [
        '[data-form-id="' + formId + '"]',
        '[data-testid*="' + formId + '"]',
        '[id*="' + formId + '"]',
        '[class*="' + formId + '"]',
        '.klaviyo-form-' + formId,
      ].join(',') + '{display:none!important;visibility:hidden!important;}';
      document.head.appendChild(style);
    }

    function attrsMentionForm(el) {
      if (!el || el.nodeType !== 1) return false;
      var attrs = ['id', 'class', 'data-form-id', 'data-testid', 'aria-label'];
      for (var i = 0; i < attrs.length; i++) {
        var value = el.getAttribute(attrs[i]) || '';
        if (value.indexOf(formId) !== -1) return true;
      }
      return false;
    }

    function textMentionsForm(el) {
      var text = el && el.textContent ? el.textContent : '';
      if (!text) return false;
      for (var i = 0; i < markers.length; i++) {
        if (text.indexOf(markers[i]) !== -1) return true;
      }
      return false;
    }

    function isKlaviyoNode(el) {
      if (!el || el.nodeType !== 1) return false;
      var value = [
        el.id || '',
        el.className || '',
        el.getAttribute('data-testid') || '',
        el.getAttribute('aria-label') || '',
      ].join(' ').toLowerCase();
      return value.indexOf('klaviyo') !== -1 || value.indexOf('kl-private') !== -1;
    }

    function removalRoot(el) {
      var root = el;
      while (root.parentElement && root.parentElement !== document.body) {
        var parent = root.parentElement;
        if (parent.classList && parent.classList.contains('dc-root')) break;
        if (!attrsMentionForm(parent) && !isKlaviyoNode(parent) && !textMentionsForm(parent)) break;
        root = parent;
      }
      return root;
    }

    function suppress(root) {
      var scope = root && root.nodeType === 1 ? root : document;
      var selector = [
        '[data-form-id="' + formId + '"]',
        '[data-testid*="' + formId + '"]',
        '[id*="' + formId + '"]',
        '[class*="' + formId + '"]',
        '.klaviyo-form-' + formId,
        '[id*="klaviyo"]',
        '[class*="klaviyo"]',
        '[class*="kl-private-reset-css"]',
      ].join(',');
      var candidates = [];
      if (scope.matches && scope.matches(selector)) candidates.push(scope);
      scope.querySelectorAll(selector).forEach(function (el) { candidates.push(el); });
      candidates.forEach(function (el) {
        if (el.closest && el.closest('.dc-root')) return;
        if (!attrsMentionForm(el) && !textMentionsForm(el) && !(isKlaviyoNode(el) && textMentionsForm(removalRoot(el)))) return;
        var target = removalRoot(el);
        if (target && target.parentNode && target !== document.body && target !== document.documentElement) {
          target.parentNode.removeChild(target);
        }
      });
    }

    addCssGuard();
    window.addEventListener('klaviyoForms', function (e) {
      if (!e.detail || e.detail.formId !== formId) return;
      if (e.detail.type !== 'open' && e.detail.type !== 'embedOpen') return;
      setTimeout(function () { suppress(document); }, 0);
      setTimeout(function () { suppress(document); }, 100);
    });
    if (typeof MutationObserver !== 'undefined') {
      var observer = new MutationObserver(function (records) {
        records.forEach(function (record) {
          record.addedNodes.forEach(function (node) { suppress(node); });
        });
      });
      observer.observe(document.documentElement, { childList: true, subtree: true });
    }
    suppress(document);
  }

  function initVideoFacades() {
    document.querySelectorAll('[data-dc-video-id]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var id = btn.getAttribute('data-dc-video-id');
        var iframe = document.createElement('iframe');
        iframe.src = 'https://www.youtube.com/embed/' + encodeURIComponent(id) + '?autoplay=1';
        iframe.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture');
        iframe.setAttribute('allowfullscreen', '');
        iframe.setAttribute('tabindex', '-1');
        iframe.className = 'dc-video-facade__iframe';
        btn.replaceWith(iframe);
        iframe.focus();
      });
    });
  }

  // ── 12. Hub search typeahead (SEARCH INDEX + HUB TYPEAHEAD CONTRACT,
  // docs/plans/discover-artist-page-v3-plan-2026-07-09.md) ──
  //
  // Progressive enhancement over the hub's existing GET search form
  // (renderHubV3Search in server/workers/discovery/renderers.js), which is
  // left completely untouched below and stays the fallback for no-JS,
  // fetch failure, and zero matches. Fetches the generated
  // assets/discover-search-index.json LAZILY on first input focus (never on
  // page load) and builds a WAI-ARIA combobox (role=combobox on the input,
  // role=listbox/option on the suggestions) per the APG pattern — there is
  // no existing precedent for this in the theme.
  function initHubTypeahead() {
    var input = document.querySelector('.dc-v3-search__input');
    if (!input) return;
    var wrap = input.closest('.dc-v3-search__sugg-wrap');
    var listEl = wrap && wrap.querySelector('.dc-v3-search__sugg');
    if (!wrap || !listEl) return; // markup not deployed yet — GET form still works.

    var GROUP_LABEL = {
      artist: dcT('search_artist', 'Artists'),
      wts: dcT('search_wts', 'Where to see'), // locale indexes only (i18n chunk 9)
      artwork: dcT('search_artwork', 'Artworks'),
      movement: dcT('search_movement', 'Movements'),
      museum: dcT('search_museum', 'Museums'),
      city: dcT('search_place', 'Places'),
      country: dcT('search_place', 'Places'),
      century: dcT('search_era', 'Eras')
    };
    var GROUP_ORDER = { artist: 0, wts: 1, artwork: 2, movement: 3, museum: 4, city: 5, country: 5, century: 6 };
    // Locale search indexes (i18n chunk 9): discover-search-index.<locale>.json,
    // built nightly by server/workers/discovery/search-index-sync.js for the
    // locales with publish:true in server/i18n/locales.js. Links keep the
    // storefront root (SolisMarketPath) for every result, as every other link
    // on a locale page does.
    var SEARCH_LOCALES = { de: 1, sv: 1 };
    var searchLocale = String(DC_I18N.locale || (window.Shopify && window.Shopify.locale) || '').toLowerCase().split('-')[0];
    if (!SEARCH_LOCALES[searchLocale]) searchLocale = '';
    var MAX_RESULTS = 8;
    var DEBOUNCE_MS = 150; // existing convention — see initArtistsIndexFilter.

    var indexPromise = null;
    var activeIndex = -1;
    var debounceTimer = null;
    var loadStarted = false;

    // NFD diacritic fold, ported from server/workers/shared/entity-slugs.js
    // toSlug() — minus its URL-safety collapse-to-dashes pass, since this
    // folds prose for substring matching, not a URL slug.
    function fold(v) {
      return String(v == null ? '' : v)
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
    }

    // body_html (the renderer) cannot call Liquid's asset_url filter, so the
    // index URL is derived from this script's own <script src> (captured at
    // module-init time in DC_SCRIPT_SRC, since document.currentScript is no
    // longer valid by the time this runs off a focus event). Cache token:
    // this script's OWN `?v=` value. Shopify's asset CDN is immutable per
    // URL (no purge on update; hand-rolled ?d tokens hit stale cached
    // variants and never reach origin — 2026-07-11 lesson), so the only
    // reliable freshness signal is a Shopify-minted v. The index publish
    // task re-PUTs discover.js (byte-identical) after each index push
    // precisely so this v rolls and the next rendered page fetches the
    // index at a never-before-seen URL.
    function searchIndexUrl(locale) {
      if (!DC_SCRIPT_SRC) return null;
      var qs = DC_SCRIPT_SRC.split('?')[1] || '';
      var base = DC_SCRIPT_SRC.split('?')[0];
      var idx = base.replace(/discover\.js$/, locale ? 'discover-search-index.' + locale + '.json' : 'discover-search-index.json');
      if (idx === base) return null; // src didn't end in discover.js — bail safely.
      var vMatch = qs.match(/(?:^|&)v=([^&]+)/);
      var token = vMatch ? vMatch[1] : String(Date.now());
      return idx + '?v=' + token;
    }

    function fetchIndex(url) {
      return fetch(url, { credentials: 'omit' })
        .then(function (r) { if (!r.ok) throw new Error('bad status'); return r.json(); })
        .then(function (data) { if (!Array.isArray(data)) throw new Error('not an index'); return data; });
    }

    function loadIndex() {
      if (indexPromise) return indexPromise;
      var url = searchIndexUrl('');
      if (!url) { indexPromise = Promise.resolve(null); return indexPromise; }
      // A locale page tries its locale index first and falls back to the
      // English one (not published yet, or a failed fetch).
      var first = searchLocale ? fetchIndex(searchIndexUrl(searchLocale)).catch(function () { return fetchIndex(url); }) : fetchIndex(url);
      indexPromise = first.catch(function () { return null; }); // fetch failure = silent degrade to the GET form.
      return indexPromise;
    }

    function computeMatches(data, q) {
      var scored = [];
      for (var i = 0; i < data.length; i++) {
        var e = data[i];
        if (!e || !e.n || !e.h) continue;
        var nameFold = fold(e.n);
        var rank = -1;
        var ni = nameFold.indexOf(q);
        if (ni === 0) rank = 0;
        else if (ni > -1) rank = 1;
        if (rank === -1 && e.a && e.a.length) {
          for (var j = 0; j < e.a.length; j++) {
            var af = fold(e.a[j]);
            var ai = af.indexOf(q);
            if (ai === 0) { rank = 2; break; }
            if (ai > -1 && rank === -1) rank = 3;
          }
        }
        if (rank > -1) scored.push({ rank: rank, entry: e });
      }
      scored.sort(function (a, b) {
        if (a.rank !== b.rank) return a.rank - b.rank;
        var an = a.entry.n.length, bn = b.entry.n.length;
        if (an !== bn) return an - bn;
        return a.entry.n < b.entry.n ? -1 : (a.entry.n > b.entry.n ? 1 : 0);
      });
      var top = scored.slice(0, MAX_RESULTS).map(function (x) { return x.entry; });
      // Cluster the capped set by group (stable sort keeps rank order within
      // a group) so headers render contiguous, never repeated.
      return top
        .map(function (e, i) { return { e: e, i: i }; })
        .sort(function (a, b) { return (GROUP_ORDER[a.e.t] - GROUP_ORDER[b.e.t]) || (a.i - b.i); })
        .map(function (x) { return x.e; });
    }

    function closeDropdown() {
      listEl.hidden = true;
      listEl.innerHTML = '';
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      activeIndex = -1;
    }

    function updateActiveDescendant(items) {
      items.forEach(function (el, i) {
        var active = i === activeIndex;
        el.setAttribute('aria-selected', active ? 'true' : 'false');
        el.classList.toggle('is-active', active);
      });
      if (activeIndex > -1 && items[activeIndex]) {
        input.setAttribute('aria-activedescendant', items[activeIndex].id);
      } else {
        input.removeAttribute('aria-activedescendant');
      }
    }

    function renderResults(matches) {
      activeIndex = -1;
      input.removeAttribute('aria-activedescendant');
      if (!matches.length) { closeDropdown(); return; }
      var html = '';
      var lastGroup = null;
      matches.forEach(function (m, i) {
        var g = GROUP_LABEL[m.t] || '';
        if (g !== lastGroup) {
          html += '<li class="dc-v3-search__sugg-group" role="presentation">' + fEsc(g) + '</li>';
          lastGroup = g;
        }
        html += '<li class="dc-v3-search__sugg-item" id="dc-v3-search-opt-' + i + '" role="option" aria-selected="false" data-href="' + fEsc(window.SolisMarketPath('/pages/' + m.h)) + '">' +
          '<span class="dc-v3-search__sugg-name">' + fEsc(m.n) + '</span>' +
          (m.s ? '<span class="dc-v3-search__sugg-sub">' + fEsc(m.s) + '</span>' : '') +
          '</li>';
      });
      listEl.innerHTML = html;
      listEl.hidden = false;
      input.setAttribute('aria-expanded', 'true');
    }

    function runSearch() {
      var q = fold(input.value.trim());
      if (!q) { closeDropdown(); return; }
      loadIndex().then(function (data) {
        if (!data) return; // unsupported/failed fetch — GET form remains the path.
        // Bail if the input changed since this search kicked off (fast typer).
        if (fold(input.value.trim()) !== q) return;
        renderResults(computeMatches(data, q));
      });
    }

    input.addEventListener('focus', function () {
      if (loadStarted) return;
      loadStarted = true;
      loadIndex();
    });

    input.addEventListener('input', function () {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(runSearch, DEBOUNCE_MS);
    });

    input.addEventListener('keydown', function (e) {
      if (listEl.hidden) return;
      var items = listEl.querySelectorAll('.dc-v3-search__sugg-item');
      if (e.key === 'ArrowDown') {
        if (!items.length) return;
        e.preventDefault();
        activeIndex = (activeIndex + 1) % items.length;
        updateActiveDescendant(items);
      } else if (e.key === 'ArrowUp') {
        if (!items.length) return;
        e.preventDefault();
        activeIndex = (activeIndex - 1 + items.length) % items.length;
        updateActiveDescendant(items);
      } else if (e.key === 'Enter') {
        if (activeIndex > -1 && items[activeIndex]) {
          // Active suggestion selected — navigate instead of submitting the form.
          e.preventDefault();
          window.location.href = items[activeIndex].getAttribute('data-href');
        }
        // No active suggestion — fall through to the native GET submit.
      } else if (e.key === 'Escape') {
        closeDropdown();
      }
    });

    listEl.addEventListener('click', function (e) {
      var li = e.target.closest('.dc-v3-search__sugg-item');
      if (!li) return;
      window.location.href = li.getAttribute('data-href');
    });

    // Click-outside closes the dropdown — same pattern as
    // initArtistsIndexFilter's dropdown-close listener above.
    document.addEventListener('click', function (e) {
      if (!wrap.contains(e.target)) closeDropdown();
    });
  }

  function init() {
    initKlaviyoSuppression();
    initReveal();
    initCarousels();
    initLightbox();
    initVideoFacades();
    initInstrumentation();
    initFacetedNav();
    initArtistsIndexFilter();
    initCiteCards();
    initHubTypeahead();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

// search-index: 214cc00757af 2026-11-04T02:16:34.884Z
